import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { createChangePasswordSchema } from '@/schemas/changePasswordSchema.ts';
import type { ChangePasswordFormValues } from '@/schemas/changePasswordSchema.ts';
import { useAlert } from '@/context/AlertContext.tsx';
import authService from '@/services/authService.ts';
import FormError from '@/components/shared/FormError.tsx';

export default function ChangePassword() {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const schema = useMemo(() => createChangePasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: ChangePasswordFormValues) => {
    setSubmitError(null);
    try {
      await authService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      reset();
      showAlert(t('profile.passwordChanged'), 'success');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('profile.passwordChangeError'));
    }
  };

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-6">
      <h2 className="mb-6 text-xl font-bold text-foreground">
        {t('profile.changePasswordTitle')}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col space-y-5">
        {/* Current password */}
        <div>
          <label
            htmlFor="currentPassword"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {t('profile.currentPasswordLabel')}
          </label>
          <div className="relative">
            <input
              id="currentPassword"
              type={showCurrent ? 'text' : 'password'}
              placeholder={t('profile.currentPasswordPlaceholder')}
              autoComplete="current-password"
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('currentPassword')}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              tabIndex={-1}
              aria-label={showCurrent ? t('common.hidePassword') : t('common.showPassword')}
            >
              {showCurrent ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </Button>
          </div>
          {errors.currentPassword && (
            <p className="mt-1 text-xs text-destructive">
              {errors.currentPassword.message}
            </p>
          )}
        </div>

        {/* New password */}
        <div>
          <label
            htmlFor="newPassword"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {t('profile.newPasswordLabel')}
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showNew ? 'text' : 'password'}
              placeholder={t('auth.passwordHint')}
              autoComplete="new-password"
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('newPassword')}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              tabIndex={-1}
              aria-label={showNew ? t('common.hidePassword') : t('common.showPassword')}
            >
              {showNew ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </Button>
          </div>
          {errors.newPassword && (
            <p className="mt-1 text-xs text-destructive">
              {errors.newPassword.message}
            </p>
          )}
        </div>

        {/* Confirm new password */}
        <div>
          <label
            htmlFor="confirmNewPassword"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {t('profile.confirmNewPasswordLabel')}
          </label>
          <div className="relative">
            <input
              id="confirmNewPassword"
              type={showConfirm ? 'text' : 'password'}
              placeholder={t('profile.confirmNewPasswordPlaceholder')}
              autoComplete="new-password"
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('confirmNewPassword')}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              tabIndex={-1}
              aria-label={showConfirm ? t('common.hidePassword') : t('common.showPassword')}
            >
              {showConfirm ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </Button>
          </div>
          {errors.confirmNewPassword && (
            <p className="mt-1 text-xs text-destructive">
              {errors.confirmNewPassword.message}
            </p>
          )}
        </div>

        <div className="mt-auto pt-0 space-y-3">
          <FormError
            message={submitError}
            onDismiss={() => setSubmitError(null)}
          />
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('profile.changePasswordSubmitting') : t('profile.changePasswordSubmit')}
          </Button>
        </div>
      </form>
    </div>
  );
}
